import React, { useState } from 'react';
import './App.css';
import Desktop from './components/Desktop';
import Window from './components/Window';
import Taskbar from './components/Taskbar';
import StartMenu from './components/StartMenu';
import Spotify from './components/Spotify';
import Van3D from './components/Van3D';
import TaskManager from './components/TaskManager';
import CrashScreen from './components/CrashScreen';
import ScreenSaver from './components/ScreenSaver';

function App() {
  const [windows, setWindows] = useState([]);
  const [minimizedWindows, setMinimizedWindows] = useState([]);
  const [deletedIcons, setDeletedIcons] = useState([]);

  const [nextWindowId, setNextWindowId] = useState(1);
  const [nextZIndex, setNextZIndex] = useState(10);
  const [isStartMenuOpen, setIsStartMenuOpen] = useState(false);
  const [showVan, setShowVan] = useState(false);
  const [isTaskManagerOpen, setIsTaskManagerOpen] = useState(false);
  const [desktopKey, setDesktopKey] = useState(0);
  const [isScreenSaverActive, setIsScreenSaverActive] = useState(false);

  // All available apps (excluding 3D Van which has special handling)
  const allApps = [
    { id: 'home', name: 'Home', icon: '/assets/icons/computer.png' },
    { id: 'about', name: 'About Me', icon: '/assets/icons/documents.png' },
    { id: 'services', name: 'Services', icon: '/assets/icons/folder.png' },
    { id: 'contact', name: 'Contact Me', icon: '/assets/icons/mail.png' },
    { id: 'calculator', name: 'Calculator', icon: '/assets/icons/calculator.png' },
    { id: 'notepad', name: 'Notepad', icon: '/assets/icons/notepad.png' },
    { id: 'file-manager', name: 'File Manager', icon: '/assets/icons/file-manager.png' },
    { id: 'minesweeper', name: 'Minesweeper', icon: '/assets/icons/minesweeper.png' },
    { id: 'solitaire', name: 'Solitaire', icon: '/assets/icons/solitaire.png' },
    { id: 'paint', name: 'Paint', icon: '/assets/icons/paint.png' },
    { id: 'media-player', name: 'Media Player', icon: '/assets/icons/media-player.png' },
    { id: 'recycle-bin', name: 'Recycle Bin', icon: '/assets/icons/recycle-bin.png' },
    { id: 'control-panel', name: 'Settings', icon: '/assets/icons/control-panel.png' },
    { id: 'ms-dos', name: 'MS-DOS', icon: '/assets/icons/ms-dos.png' },
    { id: 'spotify', name: 'Spotify', icon: '/assets/icons/compact-disc.png' },
  ];

  const getAppContent = (app, deletedIcons, onRestore, onPermanentDelete) => {
    const contents = {
      'recycle-bin': (
        <div style={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '24px', margin: 0, color: '#333' }}>Recycle Bin</h2>
            {deletedIcons.length > 0 && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => onRestore('all')}
                  style={{
                    padding: '8px 16px',
                    background: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#45a049'}
                  onMouseLeave={(e) => e.target.style.background = '#4CAF50'}
                >
                  Restore All
                </button>
                <button
                  onClick={() => onPermanentDelete('all')}
                  style={{
                    padding: '8px 16px',
                    background: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#da190b'}
                  onMouseLeave={(e) => e.target.style.background = '#f44336'}
                >
                  Delete All Permanently
                </button>
              </div>
            )}
          </div>
          {deletedIcons.length === 0 ? (
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: '16px',
              color: '#666'
            }}>
              <div style={{ fontSize: '48px', opacity: 0.3 }}>🗑️</div>
              <p style={{ fontSize: '16px' }}>Recycle Bin is empty</p>
            </div>
          ) : (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {deletedIcons.map(iconId => {
                  const deletedApp = allApps.find(a => a.id === iconId);
                  if (!deletedApp) return null;

                  return (
                    <div
                      key={iconId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px',
                        background: '#f5f5f5',
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <img
                        src={deletedApp.icon}
                        alt={deletedApp.name}
                        style={{
                          width: '32px',
                          height: '32px',
                          marginRight: '12px',
                          imageRendering: 'pixelated'
                        }}
                      />
                      <span style={{ flex: 1, fontSize: '14px', color: '#333' }}>
                        {deletedApp.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ),
      home: (
        <div style={{ padding: '20px' }}>
          <h1 style={{ fontSize: '48px', marginBottom: '16px', color: '#333' }}>
            Welcome
          </h1>
          <p style={{ fontSize: '18px', lineHeight: '1.6', color: '#666', marginBottom: '24px' }}>
            Hi, I'm a developer passionate about creating beautiful and functional web experiences.
            This OS-style interface showcases my approach to interactive design.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginTop: '32px' }}>
            <div style={{ padding: '24px', background: '#f5f5f5', borderRadius: '8px' }}>
              <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>Creative</h3>
              <p style={{ margin: 0, color: '#666' }}>Innovative solutions to complex problems</p>
            </div>
            <div style={{ padding: '24px', background: '#f5f5f5', borderRadius: '8px' }}>
              <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>Modern</h3>
              <p style={{ margin: 0, color: '#666' }}>Using cutting-edge technologies</p>
            </div>
            <div style={{ padding: '24px', background: '#f5f5f5', borderRadius: '8px' }}>
              <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>Responsive</h3>
              <p style={{ margin: 0, color: '#666' }}>Works beautifully on any device</p>
            </div>
          </div>
        </div>
      ),
      about: (
        <div style={{ padding: '20px' }}>
          <h2 style={{ fontSize: '36px', marginBottom: '24px', color: '#333' }}>About Me</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <section>
              <h3 style={{ color: '#555', marginBottom: '12px' }}>Who I Am</h3>
              <p style={{ lineHeight: '1.8', color: '#666' }}>
                I'm a full-stack developer with a passion for creating unique, interactive web experiences.
                I specialize in React, Node.js, and modern web technologies. My goal is to build applications
                that are not only functional but also delightful to use.
              </p>
            </section>
            <section>
              <h3 style={{ color: '#555', marginBottom: '12px' }}>Skills & Technologies</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {['React', 'JavaScript', 'TypeScript', 'Node.js', 'CSS/SASS', 'Git', 'REST APIs', 'UI/UX Design'].map(skill => (
                  <span key={skill} style={{ padding: '8px 16px', background: '#f0f0f0', borderRadius: '6px', fontSize: '14px', color: '#555' }}>
                    {skill}
                  </span>
                ))}
              </div>
            </section>
            <section>
              <h3 style={{ color: '#555', marginBottom: '12px' }}>Experience</h3>
              <p style={{ lineHeight: '1.8', color: '#666' }}>
                With years of experience in web development, I've worked on projects ranging from
                small business websites to complex enterprise applications. I believe in writing
                clean, maintainable code and creating user experiences that make a difference.
              </p>
            </section>
          </div>
        </div>
      ),
      services: (
        <div style={{ padding: '20px' }}>
          <h2 style={{ fontSize: '36px', marginBottom: '24px', color: '#333' }}>Services</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ padding: '24px', border: '1px solid #e0e0e0', borderRadius: '8px', transition: 'all 0.3s' }}>
              <h3 style={{ color: '#333', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>💻</span> Web Development
              </h3>
              <p style={{ lineHeight: '1.6', color: '#666', margin: 0 }}>
                Custom web applications built with modern frameworks like React, Vue, or Angular.
                Responsive, fast, and optimized for all devices.
              </p>
            </div>
            <div style={{ padding: '24px', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
              <h3 style={{ color: '#333', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🎨</span> UI/UX Design
              </h3>
              <p style={{ lineHeight: '1.6', color: '#666', margin: 0 }}>
                Beautiful, intuitive interfaces that users love. From wireframes to final designs,
                I create experiences that work seamlessly.
              </p>
            </div>
            <div style={{ padding: '24px', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
              <h3 style={{ color: '#333', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🚀</span> Performance Optimization
              </h3>
              <p style={{ lineHeight: '1.6', color: '#666', margin: 0 }}>
                Speed matters. I optimize applications for lightning-fast load times and smooth interactions
                that keep users engaged.
              </p>
            </div>
            <div style={{ padding: '24px', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
              <h3 style={{ color: '#333', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🔧</span> Consulting & Training
              </h3>
              <p style={{ lineHeight: '1.6', color: '#666', margin: 0 }}>
                Need guidance on your tech stack or want to upskill your team? I provide consulting
                and training on modern web development practices.
              </p>
            </div>
          </div>
        </div>
      ),
      contact: (
        <div style={{ padding: '20px' }}>
          <h2 style={{ fontSize: '36px', marginBottom: '24px', color: '#333' }}>Get In Touch</h2>
          <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#666', marginBottom: '32px' }}>
            I'd love to hear from you! Whether you have a project in mind, want to collaborate,
            or just want to say hi, feel free to reach out.
          </p>
          <form style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '500px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: '500' }}>Name</label>
              <input
                type="text"
                placeholder="Your name"
                style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: '500' }}>Email</label>
              <input
                type="email"
                placeholder="your.email@example.com"
                style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', color: '#333', fontWeight: '500' }}>Message</label>
              <textarea
                placeholder="Tell me about your project..."
                rows="5"
                style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>
            <button
              type="submit"
              style={{ padding: '14px 32px', background: '#333', color: 'white', border: 'none', borderRadius: '6px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={(e) => e.target.style.background = '#555'}
              onMouseLeave={(e) => e.target.style.background = '#333'}
            >
              Send Message
            </button>
          </form>
          <div style={{ marginTop: '32px', padding: '20px', background: '#f8f8f8', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: '12px', color: '#333' }}>Other Ways to Connect</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <p style={{ margin: 0, color: '#666' }}>📧 Email: hello@example.com</p>
              <p style={{ margin: 0, color: '#666' }}>💼 LinkedIn: /in/yourprofile</p>
              <p style={{ margin: 0, color: '#666' }}>🐙 GitHub: /yourusername</p>
              <p style={{ margin: 0, color: '#666' }}>🐦 Twitter: @yourhandle</p>
            </div>
          </div>
        </div>
      ),
      spotify: <Spotify />,
    };
    return contents[app.id] || <div><h2>{app.name}</h2><p>Coming soon...</p></div>;
  };

  const handleCloseWindow = (id) => {
    setWindows(windows.filter(w => w.id !== id));
    setMinimizedWindows(minimizedWindows.filter(wid => wid !== id));
  };

  const handleMinimizeWindow = (id) => {
    setMinimizedWindows([...minimizedWindows, id]);
  };

  const handleRestoreWindow = (id) => {
    setMinimizedWindows(minimizedWindows.filter(wid => wid !== id));
    handleWindowClick(id);
  };

  const handleWindowClick = (id) => {
    // Bring clicked window to front
    setWindows(windows.map(w =>
      w.id === id ? { ...w, zIndex: nextZIndex } : w
    ));
    setNextZIndex(nextZIndex + 1);
  };

  const handleStartClick = () => {
    setIsStartMenuOpen(!isStartMenuOpen);
  };

  const handleOpenApp = (app) => {
    // Special handling for clock (3D Van) - show as desktop overlay
    if (app.id === 'clock') {
      setShowVan(true);
      return;
    }

    // Check if a window with this app already exists
    const existingWindow = windows.find(w => w.title === app.name);

    if (existingWindow) {
      // If minimized, restore it
      if (minimizedWindows.includes(existingWindow.id)) {
        handleRestoreWindow(existingWindow.id);
      } else {
        // Otherwise just bring it to front
        handleWindowClick(existingWindow.id);
      }
      return;
    }

    // Create new window if it doesn't exist
    const windowWidth = Math.floor(window.innerWidth * 0.7);
    const windowHeight = Math.floor((window.innerHeight - 40) * 0.7); // subtract taskbar height (40px)
    const newWindow = {
      id: nextWindowId,
      title: app.name,
      icon: app.icon,
      zIndex: nextZIndex,
      content: getAppContent(app, deletedIcons, handleRestoreIcon, handlePermanentDelete),
      width: windowWidth,
      height: windowHeight,
      x: 100 + (nextWindowId * 30),
      y: 100 + (nextWindowId * 30)
    };
    setWindows([...windows, newWindow]);
    setNextWindowId(nextWindowId + 1);
    setNextZIndex(nextZIndex + 1);
  };

  const handleNavigateWindow = (windowId, app) => {
    // Open the selected app (same behavior as handleOpenApp)
    handleOpenApp(app);
  };

  const handleTaskManagerClick = () => {
    setIsTaskManagerOpen(!isTaskManagerOpen);
  };

  const handleCloseAllWindows = () => {
    setWindows([]);
    setMinimizedWindows([]);
    setIsTaskManagerOpen(false);
  };

  const [isCrashed, setIsCrashed] = useState(false);

  const handleRestoreIcon = (iconId) => {
    if (iconId === 'all') {
      // Close Recycle Bin window FIRST before clearing deleted icons
      setWindows(windows.filter(w => w.title !== 'Recycle Bin'));
      // Then restore all icons
      setDeletedIcons([]);
      // Reset all icon positions to default
      localStorage.removeItem('desktopIconPositions');
      // Force Desktop component to remount with fresh state
      setDesktopKey(prev => prev + 1);
    } else {
      // Restore single icon
      setDeletedIcons(deletedIcons.filter(id => id !== iconId));
      // Close any open Recycle Bin window to refresh the view
      setWindows(windows.filter(w => w.title !== 'Recycle Bin'));
    }
  };

  const handlePermanentDelete = (iconId) => {
    if (iconId === 'all') {
      // Delete all permanently
      setDeletedIcons([]);
    } else {
      // Delete single icon permanently
      setDeletedIcons(deletedIcons.filter(id => id !== iconId));
    }
    // Close all windows and trigger system crash
    setWindows([]);
    setMinimizedWindows([]);
    setIsTaskManagerOpen(false);
    setIsCrashed(true);
  };

  const handleReboot = () => {
    setIsCrashed(false);
    setDeletedIcons([]);
    setWindows([]);
    setMinimizedWindows([]);
  };

  const handleDesktopContextMenu = () => {
    return [
      {
        icon: '🎨',
        label: 'Personalize',
      },
      {
        icon: '🔄',
        label: 'Refresh',
        shortcut: 'F5',
        onClick: () => window.location.reload()
      },
    ];
  };

  const handleResetIconPositions = () => {
    // Clear localStorage to reset icon positions
    localStorage.removeItem('desktopIconPositions');
    // Force Desktop to reload by triggering a re-render
    setWindows([...windows]);
  };

  const handleSleep = () => {
    console.log('Sleep button clicked!');
    setIsScreenSaverActive(true);
    setIsStartMenuOpen(false);
  };

  const handleWakeUp = () => {
    setIsScreenSaverActive(false);
  };

  return (
    <div className="App">
      {isCrashed && <CrashScreen onReboot={handleReboot} />}
      <Desktop
        resetTrigger={desktopKey}
        onOpenApp={handleOpenApp}
        onContextMenu={handleDesktopContextMenu}
        deletedIcons={deletedIcons}
        onDeleteIcons={setDeletedIcons}
        onResetPositions={handleResetIconPositions}
      >
        {windows.map((window) => (
          !minimizedWindows.includes(window.id) && (
            <Window
              key={window.id}
              id={window.id}
              title={window.title}
              initialX={window.x}
              initialY={window.y}
              width={window.width}
              height={window.height}
              zIndex={window.zIndex}
              onClose={handleCloseWindow}
              onMinimize={() => handleMinimizeWindow(window.id)}
              onFocus={() => handleWindowClick(window.id)}
              onNavigate={(app) => handleNavigateWindow(window.id, app)}
              allApps={allApps}
            >
              {window.title === 'Recycle Bin'
                ? getAppContent(
                    { id: 'recycle-bin', name: 'Recycle Bin' },
                    deletedIcons,
                    handleRestoreIcon,
                    handlePermanentDelete
                  )
                : window.content
              }
            </Window>
          )
        ))}
        {showVan && <Van3D onClose={() => setShowVan(false)} />}
      </Desktop>
      <Taskbar
        windows={windows}
        minimizedWindows={minimizedWindows}
        onWindowClick={handleRestoreWindow}
        onStartClick={handleStartClick}
        onTaskManagerClick={handleTaskManagerClick}
      />
      <StartMenu
        isOpen={isStartMenuOpen}
        onClose={() => setIsStartMenuOpen(false)}
        onOpenApp={handleOpenApp}
        onSleep={handleSleep}
      />
      {isTaskManagerOpen && (
        <TaskManager
          windows={windows}
          onClose={() => setIsTaskManagerOpen(false)}
          onCloseAll={handleCloseAllWindows}
        />
      )}
      <ScreenSaver
        isActive={isScreenSaverActive}
        onWakeUp={handleWakeUp}
      />
    </div>
  );
}

export default App;
